// @ts-nocheck
import { useState, useEffect } from 'react';
import { supabase } from '@/lib/supabase';
import { useAuth } from '@/contexts/AuthContext';
import { toast } from 'sonner';
import { createCampCalendarEvent } from '@/utils/calendarIntegrationUtils';

export type Camp = {
  id: string;
  title: string;
  location: string;
  start_date: string;
  end_date: string;
  price_cents: number | null;
  college_id: string | null;
  is_showcase: boolean;
  is_prospect_camp: boolean;
  is_team_camp: boolean;
  sport: string | null;
  division: string | null;
  website_url: string | null;
  description: string | null;
  cost: string | null;
  gender: string | null;
  logo_url: string | null;
  isSaved?: boolean;
};

export type CampsTab = 'all' | 'upcoming' | 'saved';

export const useCamps = (activeTab: CampsTab) => {
  const [camps, setCamps] = useState<Camp[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const { profile } = useAuth();

  useEffect(() => {
    fetchCamps();
  }, [activeTab, profile?.id]);

  const fetchCamps = async () => {
    try {
      setLoading(true);
      setError(null); // Clear any previous errors
      
      console.log("Fetching camps with profile:", profile?.id);
      
      if (activeTab === 'saved' && !profile?.id) {
        console.log("No profile ID found for saved camps tab");
        setCamps([]);
        setLoading(false);
        return;
      }

      let query;
      
      // Handle different tabs with appropriate queries
      if (activeTab === 'saved') {
        // Fetch saved camps with join to get camp details
        console.log("Fetching saved camps for profile:", profile?.id);
        query = supabase
          .from('saved_camps')
          .select(`
            camp_id,
            college_camps(*)
          `)
          .eq('profile_id', profile?.id);
          
      } else {
        // Base query for all camps - always filter out past camps
        const today = new Date().toISOString().split('T')[0];
        console.log("Fetching camps, filtering out past camps before:", today);
        query = supabase
          .from('college_camps')
          .select('*')
          .or(`start_date.gte.${today},start_date.is.null`);
      }

      const { data, error } = await query;
      
      if (error) {
        console.error("Supabase query error:", error);
        throw error;
      }

      console.log(`Received ${data?.length || 0} camps from database`);

      // Process the data based on the active tab
      if (activeTab === 'saved' && data) {
        // Transform saved camps data, filtering out past camps
        const today = new Date().toISOString().split('T')[0];
        const savedCamps: Camp[] = data
          .filter(item => item.college_camps) // Filter out any null camp entries
          .map(item => ({
            ...item.college_camps,
            isSaved: true
          }))
          .filter(camp => !camp.start_date || camp.start_date >= today);
        
        console.log(`Processed ${savedCamps.length} saved camps (past camps filtered)`);
        setCamps(savedCamps);
      } else if (data) {
        // For all and upcoming tabs, fetch saved camps to mark which ones are saved
        if (profile?.id) {
          const { data: savedCampsData, error: savedCampsError } = await supabase
            .from('saved_camps')
            .select('camp_id')
            .eq('profile_id', profile.id);
            
          if (savedCampsError) {
            console.warn("Error fetching saved camp IDs:", savedCampsError);
          }
          
          // Create a set of saved camp IDs for efficient lookup
          const savedCampIds = new Set((savedCampsData || []).map(sc => sc.camp_id));
          console.log(`User has ${savedCampIds.size} saved camps`);
          
          // Mark camps as saved if they are in the saved_camps table
          setCamps(data.map(camp => ({
            ...camp,
            isSaved: savedCampIds.has(camp.id)
          })));
        } else {
          setCamps(data);
        }
      } else {
        // If no data returned, set empty array
        setCamps([]);
      }
    } catch (err: any) {
      console.error('Error fetching camps:', err);
      setError(err.message || 'Failed to fetch camps');
      toast.error('Failed to fetch camps');
      // Still set empty camps array to prevent undefined
      setCamps([]);
    } finally {
      setLoading(false);
    }
  };

  const saveCamp = async (campId: string) => {
    if (!profile?.id) {
      toast.error('You need to be logged in to save camps');
      return;
    }

    try {
      // Check if the camp is already saved
      const { data: existingData, error: existingError } = await supabase
        .from('saved_camps')
        .select('*')
        .eq('profile_id', profile.id)
        .eq('camp_id', campId)
        .maybeSingle();

      if (existingError) {
        console.error("Error checking if camp is saved:", existingError);
        throw existingError;
      }
      
      const currentCamp = camps.find(camp => camp.id === campId);
      if (!currentCamp) {
        console.warn("Attempted to save non-existent camp:", campId);
      }
      
      if (existingData) {
        // Camp already saved, so we'll remove it
        const { error: removeError } = await supabase
          .from('saved_camps')
          .delete()
          .eq('profile_id', profile.id)
          .eq('camp_id', campId);
          
        if (removeError) {
          console.error("Error removing saved camp:", removeError);
          throw removeError;
        }
        
        // Remove from calendar as well
        const { error: calendarError } = await supabase
          .from('calendar_events')
          .delete()
          .eq('profile_id', profile.id)
          .eq('related_entity_id', campId)
          .eq('source', 'camp');
        
        if (calendarError) {
          console.warn('Could not remove camp calendar event:', calendarError);
        }
        
        toast.success('Camp removed from saved camps');
      } else {
        // Camp not saved yet, so add it
        const { error: saveError } = await supabase
          .from('saved_camps')
          .insert({
            profile_id: profile.id,
            camp_id: campId,
            saved_at: new Date().toISOString()
          });
          
        if (saveError) {
          console.error("Error saving camp:", saveError);
          throw saveError;
        }
        
        // Add to calendar if we have the camp data
        if (currentCamp) {
          await createCampCalendarEvent(
            profile.id, 
            campId,
            {
              title: currentCamp.title,
              start_date: currentCamp.start_date,
              end_date: currentCamp.end_date,
              location: currentCamp.location,
              description: currentCamp.description
            }
          );
        }
        
        toast.success('Camp saved successfully');
      }

      // Update local state to reflect the change
      setCamps(camps.map(camp => 
        camp.id === campId ? { ...camp, isSaved: !camp.isSaved } : camp
      ));
    } catch (err: any) {
      console.error('Error saving camp:', err);
      toast.error('Failed to save camp');
    }
  };

  return {
    camps,
    loading,
    error,
    saveCamp,
    refresh: fetchCamps
  };
};
